import React, { useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { resolveAnyStaticUrl } from '../../../urlConfig';
import GraniteTextureRail from '../../../components/ThreeD/GraniteTextureRail';
import { isSurfacePseudoCatalog } from './graniteSurfaceFinish.utils';

const formatMoney = (value, estimated = false) => {
    const number = Number(value);

    if (!Number.isFinite(number) || number <= 0) return 'не задана';

    return `${estimated ? '≈ ' : ''}${Math.round(number).toLocaleString('ru-RU')} ₽`;
};

const formatDimensions = (dimensions) => {
    const height = Math.round(Number(dimensions?.height) || 0);
    const width = Math.round(Number(dimensions?.width) || 0);
    const thickness = Math.round(Number(dimensions?.thickness) || 0);

    if (!height || !width || !thickness) return '';

    return `${height}×${width}×${thickness} мм`;
};

const isSameDimensions = (rule, dimensions) => (
    ['height', 'width', 'thickness'].every((key) => (
        Math.round(Number(rule?.[key]) || 0) === Math.round(Number(dimensions?.[key]) || 0)
    ))
);

const getExactRuleForDimensions = (pricing, dimensions) => {
    const hasDimensions = ['height', 'width', 'thickness'].every((key) => (
        Number(dimensions?.[key]) > 0
    ));
    const rules = Array.isArray(pricing?.sizeRules) ? pricing.sizeRules : [];

    if (!hasDimensions) return null;

    return rules.find((rule) => (
        rule?.enabled !== false && isSameDimensions(rule, dimensions)
    )) || null;
};

const getStoredPriceForDimensions = (pricing, dimensions) => {
    const exactRule = getExactRuleForDimensions(pricing, dimensions);
    const candidates = [
        exactRule?.price,
        pricing?.manualPrice,
        pricing?.basePrice,
    ];

    return candidates.find((value) => Number(value) > 0) || 0;
};

const getStoredCustomerPricesForDimensions = (pricing, dimensions) => {
    const exactRule = getExactRuleForDimensions(pricing, dimensions);

    return {
        subscriberPrice: Number(exactRule?.subscriberPrice) > 0
            ? Number(exactRule.subscriberPrice)
            : 0,
        publicPrice: Number(exactRule?.publicPrice) > 0
            ? Number(exactRule.publicPrice)
            : 0,
    };
};

const getTextureUrl = (catalog) => {
    const path = String(
        catalog?.texturePreset?.path
        || catalog?.texturePreset?.url
        || ''
    ).trim();

    return path ? resolveAnyStaticUrl(path) : '';
};

const getCatalogLabel = (catalog, variant) => (
    catalog?.name || variant?.materialName || variant?.materialKey || catalog?.key || 'Гранит'
);

const getResolvedCustomerPrices = (summary = {}, variant = null, dimensions = null) => {
    const stored = getStoredCustomerPricesForDimensions(
        variant?.productPricing,
        dimensions
    );
    const subscriberPrice = Number(summary?.subscriberPrice) > 0
        ? Number(summary.subscriberPrice)
        : stored.subscriberPrice;
    const publicPrice = Number(summary?.publicPrice) > 0
        ? Number(summary.publicPrice)
        : stored.publicPrice;
    const fallbackProductPrice = Number(summary?.productPrice) > 0
        ? Number(summary.productPrice)
        : getStoredPriceForDimensions(variant?.productPricing, dimensions);

    return {
        subscriberPrice,
        publicPrice: publicPrice || fallbackProductPrice,
        fallbackProductPrice,
    };
};

const formatCustomerPrices = ({
    subscriberPrice,
    publicPrice,
    estimated = false,
}) => {
    const parts = [];

    if (Number(subscriberPrice) > 0) {
        parts.push(`Подписка ${formatMoney(subscriberPrice, estimated)}`);
    }

    if (Number(publicPrice) > 0) {
        parts.push(`Обычная ${formatMoney(publicPrice, estimated)}`);
    }

    return parts.join(' · ');
};

const formatRailCustomerPrices = ({
    subscriberPrice,
    publicPrice,
    estimated = false,
}) => {
    const parts = [];

    if (Number(subscriberPrice) > 0) {
        parts.push(`Подписка ${formatMoney(subscriberPrice, estimated)}`);
    }

    if (Number(publicPrice) > 0) {
        parts.push(`Обычная ${formatMoney(publicPrice, estimated)}`);
    }

    // GraniteTextureRail рендерит это как один text-node. Перенос строки
    // раскрывается CSS через white-space: pre-line и две цены не режутся.
    return parts.join('\n');
};

const AdminGraniteTypeSwitcher = ({
    mode = 'panel',
    catalogs = [],
    variants = [],
    activeMaterialKey = '',
    defaultMaterialKey = '',
    priceSummaryByKey = {},
    priceDisplayMode = 'product',
    commonDimensions = null,
    saving = false,
    onSelectVariant,
    onAddVariant,
    onSetDefaultVariant,
    onRemoveVariant,
    onOpenManager,
}) => {
    const [newMaterialKey, setNewMaterialKey] = useState('');
    const safeCatalogs = Array.isArray(catalogs) ? catalogs : [];
    const safeVariants = Array.isArray(variants) ? variants : [];

    const stoneCatalogs = useMemo(() => safeCatalogs.filter((catalog) => (
        catalog?.enabled !== false
        && catalog?.key
        && !isSurfacePseudoCatalog(catalog)
    )), [safeCatalogs]);

    const catalogByKey = useMemo(() => new Map(
        safeCatalogs.map((catalog) => [String(catalog?.key || ''), catalog])
    ), [safeCatalogs]);

    const variantByKey = useMemo(() => new Map(
        safeVariants
            .filter((variant) => variant?.materialKey)
            .map((variant) => [String(variant.materialKey), variant])
    ), [safeVariants]);

    const existingKeys = useMemo(() => new Set(variantByKey.keys()), [variantByKey]);

    const availableMaterials = useMemo(() => stoneCatalogs.filter((catalog) => (
        !existingKeys.has(String(catalog.key))
    )), [stoneCatalogs, existingKeys]);

    const visibleVariants = useMemo(() => safeVariants.filter((variant) => {
        const catalog = catalogByKey.get(String(variant?.materialKey || ''));

        return !isSurfacePseudoCatalog(catalog || variant);
    }), [safeVariants, catalogByKey]);

    const resolvedActiveKey = activeMaterialKey
        || defaultMaterialKey
        || visibleVariants[0]?.materialKey
        || stoneCatalogs[0]?.key
        || 'gabbro-diabase';
    const activeVariant = variantByKey.get(String(resolvedActiveKey))
        || visibleVariants[0]
        || null;
    const activeCatalog = catalogByKey.get(String(resolvedActiveKey || ''))
        || (activeVariant ? catalogByKey.get(String(activeVariant.materialKey || '')) : null);
    const activeTextureUrl = getTextureUrl(activeCatalog);
    const activeSummary = priceSummaryByKey?.[resolvedActiveKey] || {};
    const activeCustomerPrices = getResolvedCustomerPrices(
        activeSummary,
        activeVariant,
        commonDimensions
    );

    const addMaterial = () => {
        if (!newMaterialKey) return;

        onAddVariant?.(newMaterialKey);
        setNewMaterialKey('');
    };

    if (mode === 'rail') {
        const railItems = stoneCatalogs.map((catalog) => {
            const materialKey = String(catalog.key || '');
            const variant = variantByKey.get(materialKey) || null;
            const calculatedSummary = priceSummaryByKey?.[materialKey] || {};
            const customerPrices = getResolvedCustomerPrices(
                calculatedSummary,
                variant,
                commonDimensions
            );
            const millingPrice = Number(calculatedSummary.millingPrice) > 0
                ? Number(calculatedSummary.millingPrice)
                : getStoredPriceForDimensions(variant?.millingPricing, commonDimensions);
            const showMilling = priceDisplayMode === 'work';
            const visiblePrice = showMilling
                ? millingPrice
                : (customerPrices.publicPrice || customerPrices.subscriberPrice);
            const sizeLabel = formatDimensions(
                commonDimensions
                || variant?.productPricing?.baseDimensions
                || variant?.millingPricing?.baseDimensions
            );
            const customerPriceLabel = formatRailCustomerPrices({
                ...customerPrices,
                estimated: calculatedSummary.productEstimated === true,
            });

            return {
                key: materialKey,
                label: getCatalogLabel(catalog, variant),
                textureUrl: getTextureUrl(catalog),
                isDefault: materialKey === defaultMaterialKey || variant?.isDefault === true,
                muted: !variant,
                configured: !!variant && Number(visiblePrice) > 0,
                priceLabel: variant && Number(visiblePrice) > 0
                    ? showMilling
                        ? formatMoney(millingPrice)
                        : customerPriceLabel
                    : calculatedSummary.productRequestRequired === true
                        ? 'Запросить цену'
                        : 'Не настроено',
                sizeLabel,
                hint: variant
                    ? showMilling
                        ? 'цена фрезеровки'
                        : calculatedSummary.productEstimated === true
                            ? 'две клиентские цены · предварительный расчёт'
                            : 'цены для подписки и обычной продажи'
                    : 'вид гранита ещё не добавлен к товару',
            };
        });
        const showMilling = priceDisplayMode === 'work';

        return (
            <GraniteTextureRail
                items={railItems}
                activeKey={resolvedActiveKey}
                disabled={saving}
                appearance="stacked-cards"
                collapsible
                storageKey="admin-product-3d-granite-rail-collapsed"
                collapseLabel="Гранит"
                contextLabel={showMilling ? 'Фрезеровка' : 'Подписка / обычная цена'}
                onChange={(materialKey) => onSelectVariant?.(materialKey)}
                onManage={onOpenManager}
                ariaLabel={showMilling
                    ? 'Быстрый выбор гранита и цены фрезеровки'
                    : 'Быстрый выбор гранита и двух цен изделия'}
            />
        );
    }

    if (mode === 'compact') {
        const activeCustomerLabel = formatCustomerPrices({
            ...activeCustomerPrices,
            estimated: activeSummary.productEstimated === true,
        });

        return (
            <section className="adminGraniteTypeSwitcher adminGraniteTypeSwitcher--compact" aria-label="Активный вид гранита">
                <div
                    className={`adminGraniteTypeSwitcher__compactSwatch ${activeTextureUrl ? 'has-texture' : ''}`}
                    style={activeTextureUrl ? { backgroundImage: `url(${activeTextureUrl})` } : undefined}
                    aria-hidden="true"
                >
                    {!activeTextureUrl ? '◇' : null}
                </div>

                <label className="adminGraniteTypeSwitcher__compactField">
                    <span>Цена и текстура для</span>
                    <select
                        value={resolvedActiveKey}
                        onChange={(event) => onSelectVariant?.(event.target.value)}
                        disabled={saving || !visibleVariants.length}
                    >
                        {visibleVariants.map((variant) => {
                            const catalog = catalogByKey.get(String(variant?.materialKey || ''));

                            return (
                                <option key={variant.materialKey} value={variant.materialKey}>
                                    {getCatalogLabel(catalog, variant)}
                                </option>
                            );
                        })}
                    </select>
                    <small>
                        {activeCustomerLabel || 'Цена изделия не настроена'} · Фрезеровка: {formatMoney(activeSummary.millingPrice)}
                    </small>
                </label>

                <button
                    type="button"
                    className="adminGraniteTypeSwitcher__manageButton"
                    onClick={onOpenManager}
                    disabled={saving}
                    title="Открыть виды гранита"
                    aria-label="Открыть виды гранита"
                >
                    <SettingsOutlinedIcon fontSize="small" />
                </button>
            </section>
        );
    }

    return (
        <section className="adminGraniteTypeSwitcher adminGraniteTypeSwitcher--panel" aria-label="Виды гранита товара">
            <div className="adminGraniteTypeSwitcher__head">
                <div>
                    <strong>Виды гранита</strong>
                    <span>
                        Выбранный вид сразу меняет текстуру 3D и открывает его собственные цены изделия и фрезеровки.
                    </span>
                </div>

                <div className="adminGraniteTypeSwitcher__add">
                    <select
                        value={newMaterialKey}
                        onChange={(event) => setNewMaterialKey(event.target.value)}
                        disabled={saving || !availableMaterials.length}
                        aria-label="Вид гранита для добавления"
                    >
                        <option value="">
                            {availableMaterials.length ? 'Добавить вид гранита…' : 'Все виды добавлены'}
                        </option>
                        {availableMaterials.map((catalog) => (
                            <option key={catalog.key} value={catalog.key}>
                                {catalog.name}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={addMaterial}
                        disabled={saving || !newMaterialKey}
                    >
                        <AddIcon fontSize="small" />
                        <span>Добавить</span>
                    </button>
                </div>
            </div>

            <div className="adminGraniteTypeSwitcher__list">
                {visibleVariants.map((variant) => {
                    const catalog = catalogByKey.get(String(variant?.materialKey || ''));
                    const textureUrl = getTextureUrl(catalog);
                    const isActive = variant?.materialKey === resolvedActiveKey;
                    const isDefault = variant?.materialKey === defaultMaterialKey || variant?.isDefault === true;
                    const summary = priceSummaryByKey?.[variant.materialKey] || {};
                    const customerPrices = getResolvedCustomerPrices(
                        summary,
                        variant,
                        commonDimensions
                    );
                    const customerPriceLabel = formatCustomerPrices({
                        ...customerPrices,
                        estimated: summary.productEstimated === true,
                    });

                    return (
                        <article
                            className={`adminGraniteTypeSwitcher__item ${isActive ? 'is-active' : ''}`}
                            key={variant.materialKey}
                        >
                            <button
                                type="button"
                                className="adminGraniteTypeSwitcher__itemMain"
                                onClick={() => onSelectVariant?.(variant.materialKey)}
                                disabled={saving}
                            >
                                <span
                                    className={`adminGraniteTypeSwitcher__swatch ${textureUrl ? 'has-texture' : ''}`}
                                    style={textureUrl ? { backgroundImage: `url(${textureUrl})` } : undefined}
                                    aria-hidden="true"
                                >
                                    {!textureUrl ? '◇' : null}
                                </span>

                                <span className="adminGraniteTypeSwitcher__itemText">
                                    <span className="adminGraniteTypeSwitcher__itemTitle">
                                        <strong>{getCatalogLabel(catalog, variant)}</strong>
                                        {isDefault ? <em>По умолчанию</em> : null}
                                    </span>
                                    <small>
                                        Изделие: {summary.productRequestRequired && !customerPriceLabel
                                            ? 'запросить цену'
                                            : (customerPriceLabel || 'не задана')}
                                    </small>
                                    <small>
                                        Фрезеровка: {formatMoney(summary.millingPrice)}
                                    </small>
                                </span>
                            </button>

                            <div className="adminGraniteTypeSwitcher__actions">
                                <button
                                    type="button"
                                    className="is-default"
                                    onClick={() => onSetDefaultVariant?.(variant.materialKey)}
                                    disabled={saving || isDefault}
                                    title={isDefault ? 'Уже выбран по умолчанию' : 'Сделать видом по умолчанию'}
                                    aria-label="Сделать видом гранита по умолчанию"
                                >
                                    <StarRoundedIcon fontSize="small" />
                                </button>
                                <button
                                    type="button"
                                    className="is-remove"
                                    onClick={() => onRemoveVariant?.(variant.materialKey)}
                                    disabled={saving || visibleVariants.length <= 1}
                                    title="Убрать вид гранита из товара"
                                    aria-label="Убрать вид гранита из товара"
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </button>
                            </div>
                        </article>
                    );
                })}
            </div>

            {!visibleVariants.length ? (
                <div className="adminGraniteTypeSwitcher__empty">
                    У товара нет доступных видов гранита. Добавь габбро-диабаз или другой камень из справочника.
                </div>
            ) : null}
        </section>
    );
};

export default AdminGraniteTypeSwitcher;
